import React, { useState, useCallback, useEffect } from "react";
import {
  Badge, Button, Card, Col, Container,
  Form, Row, Spinner, Table, ButtonGroup,
} from "react-bootstrap";
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import axios from "../utils/api";
import toast from "react-hot-toast";
import "../styles/SalesDashboard.css";

const API_URL = process.env.REACT_APP_API_URL_BASE || "http://localhost:6020/";

const RANGES = [
  { key: "7d",  label: "7 Days" },
  { key: "30d", label: "30 Days" },
  { key: "90d", label: "90 Days" },
  { key: "ytd", label: "Year to Date" },
  { key: "all", label: "All Time" },
];

const TREND_VIEWS = [
  { key: "daily",   label: "Daily" },
  { key: "weekly",  label: "Weekly" },
  { key: "monthly", label: "Monthly" },
];

const COLORS = [
  "#6366f1", "#06b6d4", "#10b981", "#f59e0b", "#ef4444",
  "#8b5cf6", "#ec4899", "#14b8a6", "#f97316", "#64748b",
  "#a855f7", "#22d3ee", "#34d399", "#fbbf24", "#f87171",
];

const fmt = (n) => {
  if (n == null) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency", currency: "USD", minimumFractionDigits: 0, maximumFractionDigits: 0,
  }).format(n);
};

const fmtNum = (n) => {
  if (n == null) return "—";
  return new Intl.NumberFormat("en-US").format(n);
};

const fmtPct = (n) => {
  if (n == null) return null;
  const sign = n > 0 ? "+" : "";
  return `${sign}${n}%`;
};

/* ── KPI Card ──────────────────────────────────────────────────────────── */
const KpiCard = ({ icon, title, value, change, prefix = "" }) => {
  const changeColor = change > 0 ? "text-success" : change < 0 ? "text-danger" : "text-muted";
  return (
    <Card className="sd-kpi-card h-100">
      <Card.Body className="d-flex flex-column">
        <div className="d-flex align-items-center justify-content-between mb-2">
          <span className="sd-kpi-icon">{icon}</span>
          {change != null && (
            <Badge bg="none" className={`sd-change-badge ${changeColor}`}>
              {change > 0 ? "▲" : change < 0 ? "▼" : "–"} {fmtPct(change)}
            </Badge>
          )}
        </div>
        <div className="sd-kpi-value">{prefix}{value}</div>
        <div className="sd-kpi-title">{title}</div>
      </Card.Body>
    </Card>
  );
};

/* ── Custom Tooltip ────────────────────────────────────────────────────── */
const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="sd-chart-tooltip">
      <div className="sd-tooltip-label">{label}</div>
      {payload.map((p, i) => (
        <div key={i} className="sd-tooltip-row" style={{ color: p.color }}>
          <span className="sd-tooltip-dot" style={{ backgroundColor: p.color }} />
          {p.name}: {p.name.toLowerCase().includes("revenue") || p.name.toLowerCase().includes("total")
            ? fmt(p.value) : fmtNum(p.value)}
        </div>
      ))}
    </div>
  );
};

/* ── Hour label ────────────────────────────────────────────────────────── */
const hourLabel = (h) => {
  if (h === 0) return "12am";
  if (h < 12) return `${h}am`;
  if (h === 12) return "12pm";
  return `${h - 12}pm`;
};

/* ─────────────────────────────────────────────────────────────────────── */
/* ── MAIN COMPONENT ──────────────────────────────────────────────────── */
/* ─────────────────────────────────────────────────────────────────────── */
function SalesDashboard() {
  const [range, setRange]       = useState("30d");
  const [trendView, setTrendView] = useState("daily");
  const [loading, setLoading]   = useState(false);
  const [data, setData]         = useState(null);

  const fetchDashboard = useCallback(async (r = range) => {
    setLoading(true);
    try {
      const { data: resp } = await axios.get(`${API_URL}api/sales/dashboard`, {
        params: { range: r, topN: 20 },
      });
      if (!resp.success) throw new Error(resp.message || "Failed to load");
      setData(resp);
    } catch (err) {
      const msg = err?.response?.data?.message || err.message || "Error";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => {
    fetchDashboard();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRangeChange = (r) => {
    setRange(r);
    fetchDashboard(r);
  };

  const ov = data?.overview || {};

  const trendData =
    trendView === "weekly"  ? (data?.weeklyTrend  || []).map((d) => ({ ...d, name: d.week })) :
    trendView === "monthly" ? (data?.monthlyTrend || []).map((d) => ({ ...d, name: d.month })) :
                              (data?.dailyTrend   || []).map((d) => ({ ...d, name: d.date?.slice(5) }));

  // Top departments for pie chart (take top 8, rest as "Other")
  const deptData = (() => {
    const depts = data?.departments || [];
    if (depts.length <= 8) return depts;
    const top = depts.slice(0, 7);
    const other = depts.slice(7).reduce(
      (acc, d) => ({ ...acc, revenue: acc.revenue + d.revenue, qty: acc.qty + d.qty }),
      { department: "Other", revenue: 0, qty: 0, uniqueItems: 0 }
    );
    return [...top, other];
  })();

  const hourlyData = (() => {
    const map = {};
    (data?.hourly || []).forEach((h) => { map[h.hour] = h; });
    return Array.from({ length: 24 }, (_, i) => ({
      hour: i,
      label: hourLabel(i),
      revenue: map[i]?.revenue || 0,
      transactions: map[i]?.transactions || 0,
    }));
  })();

  return (
    <Container fluid className="sd-container py-4">
      {/* ── Header ──────────────────────────────────────────────────── */}
      <div className="d-flex flex-wrap align-items-center justify-content-between mb-4">
        <div>
          <h2 className="sd-title mb-0">📊 Sales Dashboard</h2>
          <div className="text-muted small">
            POS transaction analytics{data ? ` • ${fmtNum(ov.totalTransactions)} line items` : ""}
          </div>
        </div>
        <div className="d-flex align-items-center gap-3 mt-2 mt-md-0">
          <ButtonGroup size="sm">
            {RANGES.map((r) => (
              <Button
                key={r.key}
                variant={range === r.key ? "primary" : "outline-secondary"}
                onClick={() => handleRangeChange(r.key)}
                disabled={loading}
              >
                {r.label}
              </Button>
            ))}
          </ButtonGroup>
          <Button variant="outline-primary" size="sm" onClick={() => fetchDashboard()} disabled={loading}>
            {loading ? <Spinner animation="border" size="sm" /> : "⟳ Refresh"}
          </Button>
        </div>
      </div>

      {loading && !data && (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <div className="mt-2 text-muted">Loading analytics…</div>
        </div>
      )}

      {data && (
        <>
          {/* ── KPI Row ────────────────────────────────────────────────── */}
          <Row className="g-3 mb-4">
            <Col xs={6} md={4} xl={2}>
              <KpiCard icon="💰" title="Total Revenue" value={fmt(ov.totalRevenue)} change={ov.revenueChange} />
            </Col>
            <Col xs={6} md={4} xl={2}>
              <KpiCard icon="🛒" title="Transactions" value={fmtNum(ov.uniqueBaskets)} change={ov.basketChange} />
            </Col>
            <Col xs={6} md={4} xl={2}>
              <KpiCard icon="📦" title="Units Sold" value={fmtNum(ov.totalQty)} change={ov.qtyChange} />
            </Col>
            <Col xs={6} md={4} xl={2}>
              <KpiCard icon="🏷️" title="Avg Basket" value={fmt(ov.avgBasketSize)} />
            </Col>
            <Col xs={6} md={4} xl={2}>
              <KpiCard icon="🎁" title="Discounts" value={fmt(ov.totalDiscount)} />
            </Col>
            <Col xs={6} md={4} xl={2}>
              <KpiCard icon="📋" title="Unique SKUs" value={fmtNum(ov.uniqueItems)} />
            </Col>
          </Row>

          {/* ── Revenue Trend ──────────────────────────────────────────── */}
          <Card className="sd-chart-card mb-4">
            <Card.Body>
              <div className="d-flex align-items-center justify-content-between mb-3">
                <h5 className="mb-0">Revenue Trend</h5>
                <ButtonGroup size="sm">
                  {TREND_VIEWS.map((tv) => (
                    <Button
                      key={tv.key}
                      variant={trendView === tv.key ? "primary" : "outline-secondary"}
                      onClick={() => setTrendView(tv.key)}
                    >
                      {tv.label}
                    </Button>
                  ))}
                </ButtonGroup>
              </div>
              <ResponsiveContainer width="100%" height={320}>
                <AreaChart data={trendData}>
                  <defs>
                    <linearGradient id="gradRevenue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 11 }} />
                  <Tooltip content={<ChartTooltip />} />
                  <Area
                    type="monotone" dataKey="revenue" name="Revenue"
                    stroke="#6366f1" fill="url(#gradRevenue)" strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </Card.Body>
          </Card>

          {/* ── Transactions + Units overlay ───────────────────────────── */}
          <Row className="g-3 mb-4">
            <Col lg={8}>
              <Card className="sd-chart-card h-100">
                <Card.Body>
                  <h5 className="mb-3">Transactions & Units</h5>
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={trendData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                      <YAxis yAxisId="left" tick={{ fontSize: 11 }} />
                      <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} />
                      <Tooltip content={<ChartTooltip />} />
                      <Legend />
                      <Bar yAxisId="left" dataKey="transactions" name="Transactions" fill="#06b6d4" radius={[4, 4, 0, 0]} />
                      <Bar yAxisId="right" dataKey="qty" name="Units" fill="#10b981" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Card.Body>
              </Card>
            </Col>

            {/* ── Day-of-Week ──────────────────────────────────────────── */}
            <Col lg={4}>
              <Card className="sd-chart-card h-100">
                <Card.Body>
                  <h5 className="mb-3">Sales by Day of Week</h5>
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={data?.dayOfWeek || []} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis type="number" tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 11 }} />
                      <YAxis type="category" dataKey="day" tick={{ fontSize: 12 }} width={36} />
                      <Tooltip content={<ChartTooltip />} />
                      <Bar dataKey="revenue" name="Revenue" fill="#8b5cf6" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* ── Hourly Heatmap + Dept Pie ──────────────────────────────── */}
          <Row className="g-3 mb-4">
            <Col lg={8}>
              <Card className="sd-chart-card h-100">
                <Card.Body>
                  <h5 className="mb-3">Hourly Sales Pattern</h5>
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={hourlyData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="label" tick={{ fontSize: 10 }} interval={1} />
                      <YAxis tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 11 }} />
                      <Tooltip content={<ChartTooltip />} />
                      <Bar dataKey="revenue" name="Revenue" radius={[4, 4, 0, 0]}>
                        {hourlyData.map((entry, i) => (
                          <Cell
                            key={i}
                            fill={
                              entry.revenue === 0 ? "#e2e8f0" :
                              entry.revenue < (Math.max(...hourlyData.map(h => h.revenue)) * 0.33) ? "#93c5fd" :
                              entry.revenue < (Math.max(...hourlyData.map(h => h.revenue)) * 0.66) ? "#6366f1" :
                              "#4338ca"
                            }
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Card.Body>
              </Card>
            </Col>

            <Col lg={4}>
              <Card className="sd-chart-card h-100">
                <Card.Body>
                  <h5 className="mb-3">Revenue by Department</h5>
                  <ResponsiveContainer width="100%" height={280}>
                    <PieChart>
                      <Pie
                        data={deptData}
                        dataKey="revenue"
                        nameKey="department"
                        cx="50%" cy="50%"
                        innerRadius={50} outerRadius={90}
                        paddingAngle={2}
                        label={({ department, percent }) => `${department} ${(percent * 100).toFixed(0)}%`}
                        labelLine={false}
                        style={{ fontSize: 10 }}
                      >
                        {deptData.map((_, i) => (
                          <Cell key={i} fill={COLORS[i % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(val) => fmt(val)} />
                    </PieChart>
                  </ResponsiveContainer>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* ── Top Sellers ────────────────────────────────────────────── */}
          <Card className="sd-chart-card mb-4">
            <Card.Body>
              <h5 className="mb-3">🏆 Top Sellers</h5>
              <div className="table-responsive" style={{ maxHeight: 420, overflowY: "auto" }}>
                <Table striped hover size="sm" className="mb-0 sd-table">
                  <thead className="table-light sticky-top">
                    <tr>
                      <th style={{ width: 40 }}>#</th>
                      <th>Item</th>
                      <th>UPC</th>
                      <th>Dept</th>
                      <th className="text-end">Revenue</th>
                      <th className="text-end">Qty</th>
                      <th className="text-end">Avg Price</th>
                      <th className="text-end">Txns</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data?.topSellers || []).map((s, i) => (
                      <tr key={s.upc || i}>
                        <td>
                          {i < 3 ? (
                            <span className="sd-rank-medal">{["🥇", "🥈", "🥉"][i]}</span>
                          ) : (
                            <span className="text-muted">{i + 1}</span>
                          )}
                        </td>
                        <td className="fw-medium">{s.item || "—"}</td>
                        <td><code className="small">{s.upc}</code></td>
                        <td><Badge bg="secondary" className="fw-normal">{s.dept || "—"}</Badge></td>
                        <td className="text-end fw-semibold text-success">{fmt(s.revenue)}</td>
                        <td className="text-end">{fmtNum(s.qty)}</td>
                        <td className="text-end">{fmt(s.avgPrice)}</td>
                        <td className="text-end">{fmtNum(s.txns)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>

          {/* ── Department Breakdown Table ──────────────────────────────── */}
          <Row className="g-3 mb-4">
            <Col lg={6}>
              <Card className="sd-chart-card h-100">
                <Card.Body>
                  <h5 className="mb-3">Department Breakdown</h5>
                  <div className="table-responsive" style={{ maxHeight: 360, overflowY: "auto" }}>
                    <Table striped hover size="sm" className="mb-0 sd-table">
                      <thead className="table-light sticky-top">
                        <tr>
                          <th>Department</th>
                          <th className="text-end">Revenue</th>
                          <th className="text-end">Units</th>
                          <th className="text-end">SKUs</th>
                          <th style={{ width: 140 }}>Share</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(data?.departments || []).map((d, i) => {
                          const totalRev = ov.totalRevenue || 1;
                          const pct = ((d.revenue / totalRev) * 100).toFixed(1);
                          return (
                            <tr key={d.department || i}>
                              <td className="fw-medium">{d.department || "—"}</td>
                              <td className="text-end">{fmt(d.revenue)}</td>
                              <td className="text-end">{fmtNum(d.qty)}</td>
                              <td className="text-end">{fmtNum(d.uniqueItems)}</td>
                              <td>
                                <div className="d-flex align-items-center gap-2">
                                  <div className="sd-bar-bg">
                                    <div
                                      className="sd-bar-fill"
                                      style={{
                                        width: `${pct}%`,
                                        backgroundColor: COLORS[i % COLORS.length],
                                      }}
                                    />
                                  </div>
                                  <span className="small text-muted">{pct}%</span>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            {/* ── Recent Transactions ──────────────────────────────────── */}
            <Col lg={6}>
              <Card className="sd-chart-card h-100">
                <Card.Body>
                  <h5 className="mb-3">Recent Transactions</h5>
                  <div className="table-responsive" style={{ maxHeight: 360, overflowY: "auto" }}>
                    <Table striped hover size="sm" className="mb-0 sd-table">
                      <thead className="table-light sticky-top">
                        <tr>
                          <th>Basket</th>
                          <th>Date</th>
                          <th className="text-end">Items</th>
                          <th className="text-end">Qty</th>
                          <th className="text-end">Total</th>
                          <th className="text-end">Disc</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(data?.recentTxns || []).map((t, i) => (
                          <tr key={t.basketId || i}>
                            <td><code className="small">{t.basketId}</code></td>
                            <td className="small">{t.entered}</td>
                            <td className="text-end">{t.items}</td>
                            <td className="text-end">{t.totalQty}</td>
                            <td className="text-end fw-semibold">{fmt(t.total)}</td>
                            <td className="text-end text-danger">
                              {t.discount ? fmt(t.discount) : "—"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </>
      )}
    </Container>
  );
}

export default SalesDashboard;
